import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { ConfirmationAction } from '../../types/chat';

interface ConfirmationCardProps {
  confirmation: ConfirmationAction;
  onConfirm: (actionId: string) => void;
  onCancel: (actionId: string) => void;
  isLoading?: boolean;
  isResolved?: boolean;
}

export const ConfirmationCard = ({
  confirmation,
  onConfirm,
  onCancel,
  isLoading = false,
  isResolved = false,
}: ConfirmationCardProps) => {
  // Parse summary lines: "Action: Invoice #INV-001 | Vendor: Acme | Amount: $100"
  const parts = confirmation.summary.split('|').map((p) => p.trim());
  const title = parts[0] ?? confirmation.summary;
  const details = parts.slice(1);

  return (
    <div className="mt-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1">
        Confirm Action
      </p>
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
      {details.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {details.map((d, i) => (
            <span key={i} className="text-xs text-gray-500 dark:text-gray-400">{d}</span>
          ))}
        </div>
      )}

      {!isResolved && (
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => onConfirm(confirmation.action_id)}
            disabled={isLoading}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              'bg-[rgb(var(--color-primary-rgb))] text-white',
              'hover:opacity-90',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" />
            )}
            Confirm
          </button>
          <button
            onClick={() => onCancel(confirmation.action_id)}
            disabled={isLoading}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              'border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400',
              'hover:bg-red-50 dark:hover:bg-red-950/30',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <XCircle className="h-3.5 w-3.5" />
            Cancel
          </button>
        </div>
      )}

      {isResolved && (
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 italic">Action resolved.</p>
      )}
    </div>
  );
};
